var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BkperError } from "../model/BkperError.js";
import { HttpRequest } from "./http-request.js";
/**
 * The official Bkper API base URL.
 */
export const API_BASE_URL = "https://api.bkper.app";
/**
 * Resolves the base URL based on config.
 */
export function resolveBaseUrl(config) {
    if (config.apiBaseUrl) {
        return config.apiBaseUrl;
    }
    return API_BASE_URL;
}
export class HttpApiRequest extends HttpRequest {
    constructor(path, config) {
        const baseUrl = resolveBaseUrl(config);
        super(`${baseUrl}/${path}`);
        this.retry = 0;
        this.config = config;
        this.requestPath = path;
    }
    fetch() {
        const _super = Object.create(null, {
            execute: { get: () => super.execute }
        });
        return __awaiter(this, void 0, void 0, function* () {
            this.addCustomHeaders();
            yield this.addAgentIdHeader();
            const accessToken = yield this.getAccessToken();
            if (accessToken) {
                this.setHeader("Authorization", `Bearer ${accessToken}`);
            }
            yield this.addApiKeyHeader();
            try {
                let resp = yield _super.execute.call(this);
                if (resp.status >= 200 && resp.status < 300) {
                    return resp;
                }
                else if (resp.status == 404) {
                    const errorObj = {
                        response: {
                            status: resp.status,
                            data: resp.data,
                        },
                    };
                    throw this.handleError(errorObj);
                }
                else if (this.shouldRetry(resp.status) && this.retry < 3) {
                    this.retry++;
                    const effectiveConfig = this.config;
                    if (effectiveConfig.requestRetryHandler) {
                        yield effectiveConfig.requestRetryHandler(resp.status, resp.data, this.retry);
                    }
                    else {
                        console.log(`${JSON.stringify(resp.data)} - Retrying ${this.retry}/3... `);
                    }
                    return yield this.fetch();
                }
                else {
                    // Create an error object that matches axios error structure for compatibility
                    const errorObj = {
                        response: {
                            status: resp.status,
                            data: resp.data,
                        },
                    };
                    throw this.handleError(errorObj);
                }
            }
            catch (error) {
                if (error instanceof BkperError) {
                    throw error;
                }
                // If error already has response structure (from upstream), preserve it
                if (error.response) {
                    throw error;
                }
                // Network error or fetch failure
                if (this.isNetworkError(error)) {
                    // Network error - retry if within retry limit
                    if (this.retry < 3) {
                        this.retry++;
                        const effectiveConfig = this.config;
                        if (effectiveConfig.requestRetryHandler) {
                            yield effectiveConfig.requestRetryHandler(520, error, this.retry);
                        }
                        else {
                            console.log(`${this.formatNetworkErrorMessage(error)} - Retrying ${this.retry}/3... `);
                        }
                        return yield this.fetch();
                    }
                }
                throw this.handleError(error);
            }
        });
    }
    shouldRetry(status) {
        // 401/403 are included so consumers can refresh the OAuth token via
        // requestRetryHandler and have the request re-issued. The retry cap
        // (this.retry < 3) prevents loops on permanent auth failures.
        return status == 401 || status == 403 || status == 408 || status == 429 || (status != null && status >= 500);
    }
    isNetworkError(error) {
        return error instanceof TypeError && error.message.toLowerCase().includes("fetch");
    }
    formatNetworkErrorMessage(error) {
        var _a, _b, _c, _d, _e;
        const details = [];
        if ((_a = error.cause) === null || _a === void 0 ? void 0 : _a.code) {
            details.push(error.cause.code);
        }
        if ((_b = error.cause) === null || _b === void 0 ? void 0 : _b.hostname) {
            details.push(error.cause.hostname);
        }
        else if ((_c = error.cause) === null || _c === void 0 ? void 0 : _c.address) {
            details.push(error.cause.address);
        }
        if (((_d = error.cause) === null || _d === void 0 ? void 0 : _d.port) !== undefined) {
            details.push(`port=${error.cause.port}`);
        }
        if ((_e = error.cause) === null || _e === void 0 ? void 0 : _e.syscall) {
            details.push(`syscall=${error.cause.syscall}`);
        }
        if (details.length > 0) {
            return `Network error calling ${this.requestPath}: ${error.message} (${details.join(", ")})`;
        }
        return `Network error calling ${this.requestPath}: ${error.message}`;
    }
    handleError(err) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const effectiveConfig = this.config;
        const customError = effectiveConfig.requestErrorHandler
            ? effectiveConfig.requestErrorHandler(err)
            : undefined;
        if (customError) {
            return customError;
        }
        else if (err instanceof BkperError) {
            return err;
        }
        else if (this.isNetworkError(err)) {
            return new BkperError(((_a = err.response) === null || _a === void 0 ? void 0 : _a.status) || ((_b = err.response) === null || _b === void 0 ? void 0 : _b.code) || err.status || err.code || 0, this.formatNetworkErrorMessage(err), undefined);
        }
        else {
            // Read internal HttpError from response
            let error = ((_d = (_c = err.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.error) || ((_e = err.data) === null || _e === void 0 ? void 0 : _e.error) || err.error;
            if (error) {
                // Transform to BkperError
                return new BkperError(error.code, error.message, (_g = (_f = error.errors) === null || _f === void 0 ? void 0 : _f[0]) === null || _g === void 0 ? void 0 : _g.reason);
            }
            else {
                // Fallback for network errors, etc.
                return new BkperError(((_h = err.response) === null || _h === void 0 ? void 0 : _h.status) || ((_j = err.response) === null || _j === void 0 ? void 0 : _j.code) || err.status || err.code || 0, err.message || String(err), undefined);
            }
        }
    }
    addCustomHeaders() {
        return __awaiter(this, void 0, void 0, function* () {
            const effectiveConfig = this.config;
            if (effectiveConfig.requestHeadersProvider) {
                const headers = yield effectiveConfig.requestHeadersProvider();
                Object.entries(headers).forEach(([key, value]) => this.setHeader(key, value));
            }
        });
    }
    addAgentIdHeader() {
        return __awaiter(this, void 0, void 0, function* () {
            const effectiveConfig = this.config;
            if (effectiveConfig.agentIdProvider) {
                const agentId = yield effectiveConfig.agentIdProvider();
                if (agentId) {
                    this.setHeader("bkper-agent-id", agentId);
                }
            }
        });
    }
    addApiKeyHeader() {
        return __awaiter(this, void 0, void 0, function* () {
            const effectiveConfig = this.config;
            if (effectiveConfig.apiKeyProvider) {
                const apiKey = yield effectiveConfig.apiKeyProvider();
                if (apiKey) {
                    this.setHeader("bkper-api-key", apiKey);
                }
            }
        });
    }
    getAccessToken() {
        return __awaiter(this, void 0, void 0, function* () {
            let token = undefined;
            const effectiveConfig = this.config;
            if (effectiveConfig.oauthTokenProvider) {
                token = yield effectiveConfig.oauthTokenProvider();
            }
            if (token) {
                token = token.replace("Bearer ", "");
                token = token.replace("bearer ", "");
            }
            return token;
        });
    }
}
export class HttpBooksApiV5Request extends HttpApiRequest {
    constructor(service, config) {
        super(`v5/books/${service}`, config);
    }
}
export class HttpApiV5Request extends HttpApiRequest {
    constructor(service, config) {
        super(`v5/${service}`, config);
    }
}
//# sourceMappingURL=http-api-request.js.map